import { getEdfHeaderInfo } from '@lmt/signal-viewer-processor';
import { type EventInterval } from '@lmt/signal-viewer-ui';
import { getCustomEvents, type CustomEventsRecord } from '../api/events-api';
import { backendEventsToIntervals, msToLocalIso } from './events-helper';
import { type PairedEventXml } from './pair-signal-events';

export type PairedUploadRecord = {
  edfFile: File;
  /** Paired by pairSignalsWithEventXmls. */
  eventXml?: PairedEventXml;
};

/**
 * Resolves events for loose custom uploads through the backend /events/custom
 * endpoint. Failures degrade to "no events" so a backend outage never blocks
 * an upload.
 */
export async function resolveCustomEvents(
  records: PairedUploadRecord[],
): Promise<EventInterval[]> {
  const requestRecords = (
    await Promise.all(
      records.map(async ({ edfFile, eventXml }) => {
        if (!eventXml) return null;

        const result = await getEdfHeaderInfo(edfFile);
        if (!result.isSuccess) {
          console.warn(
            `Skipping events of "${eventXml.fileName}": failed to read EDF header of "${edfFile.name}": ${result.error}.`,
          );
          return null;
        }

        const startLocal = msToLocalIso(result.data.startMs);
        if (startLocal === null || !Number.isFinite(result.data.duration)) {
          console.warn(
            `Skipping events of "${eventXml.fileName}": EDF header of "${edfFile.name}" has an out-of-range start or duration.`,
          );
          return null;
        }

        return {
          fileName: eventXml.fileName,
          file: eventXml.file,
          startLocal,
          durationMs: result.data.duration,
        } satisfies CustomEventsRecord;
      }),
    )
  ).filter((record) => record !== null);

  if (requestRecords.length === 0) return [];

  try {
    return backendEventsToIntervals(await getCustomEvents(requestRecords));
  } catch (error) {
    console.warn('Failed to load backend events for custom upload.', error);
    return [];
  }
}
